// Copyright 2016 Sorama B.V.

import { Point2D, Range } from "datatypes/SoramaLocalApi";
import { ArrayExtensions } from "extensions/ArrayExtensions";
import { DataPoint } from "libraries/Export/IDataExporter";
import { Frequency } from "units/Frequency";
import { Pressure } from "units/Pressure";
import { Unit, UnitValue } from "units/Unit";
import { UnitSpectrum } from "units/UnitSpectrum";
import { Weighting } from "units/Weighting";

export class WeightedSpectrum extends UnitSpectrum {
    public originalDataPoints: Point2D[];

    private _weightingLowerBound: UnitValue;

    public get weightingLowerBound(): UnitValue {
        return this._weightingLowerBound;
    }

    public set weightingLowerBound(value: UnitValue) {
        if (this.weightingLowerBound === value) return;

        this._weightingLowerBound = value;
        this.applyWeighting();
    }

    private _weighting: Weighting;

    public get weighting(): Weighting {
        return this._weighting;
    }

    public set weighting(value: Weighting) {
        if (this._weighting.name === value.name) return;

        this._weighting = value;
        this.applyWeighting();
    }

    //#region Constructors

    /**
     * Create a new weighted unit spectrum.
     * @param weighting Weighting of this spectrum.
     * @param xUnit Unit of the x values of this spectrum.
     * @param yUnit Unit of the y values of this spectrum.minimumDisplayedXUnitValue
     * @param dataPoints The (unweighted) data of this spectrum.
     * @param time Timestamp of this spectrum.
     */
    constructor(
        weighting: Weighting,
        weightingLowerBound: UnitValue,
        xUnit: Unit,
        yUnit: Unit,
        data: Point2D[],
        timeRange: Range
    ) {
        super(xUnit, yUnit, data, timeRange);

        this.originalDataPoints = data;
        this.data = data;
        this._weighting = weighting;
        this._weightingLowerBound = weightingLowerBound;
    }

    /**
     * Create a new weighted unit spectrum from an existing unit spectrum.
     * @param weighting Weighting of this spectrum.
     * @param xUnit Unit of the x values of this spectrum.
     * @param yUnit Unit of the y values of this spectrum.
     * @param unitSpectrum The UnitSpectrum to base this WeightedSpectrum on.
     */
    public static FromUnitSpectrum(weighting: Weighting, unitSpectrum: UnitSpectrum): WeightedSpectrum {
        return new WeightedSpectrum(
            weighting,
            new UnitValue(22, Frequency.hertz),
            unitSpectrum.xUnit,
            unitSpectrum.yUnit,
            unitSpectrum.data,
            unitSpectrum.properties.timeRange
        );
    }

    //#endregion

    /**
     * Computes the total power value of this weighted spectrum
     * @param unit Output unit of the power value
     * @param range Frequency range in Hertz
     */
    public toPowerValueOfRanges(unit: Unit, ranges?: Range[]): UnitValue {
        const sumAction = (from: number, to: number) => {
            let spl = 0.0;
            for (let i = from; i <= to; i++) {
                const value = new UnitValue(this.data[i].y, this.yUnit).toUnit(Pressure.pascal);
                spl += value * value;
            }
            return spl;
        };

        let powerValue = 0.0;

        if (ranges) {
            if (ranges.length === 0) return new UnitValue(0, unit); // No ranges, no power, otherwise we get -Infinity

            const transformer =
                this.xUnit === Frequency.hertz
                    ? (x: DataPoint) => x.x
                    : (x: DataPoint) => new UnitValue(x.x, this.xUnit).toUnit(Frequency.hertz);

            // Determine indices for each of the ranges
            const indices = ranges.map((range) => {
                const startIndex = ArrayExtensions.fastFindClosestValueIndex(this.data, range.from, transformer);
                const stopIndex = ArrayExtensions.fastFindClosestValueIndex(this.data, range.to, transformer);

                return { from: startIndex, to: stopIndex };
            });
            // Make sure none of the ranges overlap
            for (let i = 0; i < indices.length; i++) {
                for (let j = i + 1; j < indices.length; j++) {
                    let newFrom = indices[i].from;
                    let newTo = indices[i].to;
                    let overlap = false;

                    if (indices[i].from <= indices[j].to && indices[i].from >= indices[j].from) {
                        // Merge the from
                        newFrom = indices[j].from;
                        overlap = true;
                    }

                    if (indices[i].to <= indices[j].to && indices[i].to >= indices[j].from) {
                        // Merge the to
                        newTo = indices[j].to;
                        overlap = true;
                    }

                    if (overlap) {
                        indices[i].from = newFrom;
                        indices[i].to = newTo;
                        indices.splice(j, 1);
                        j--;
                    }
                }
            }

            // Generate the powervalue
            for (const index of indices) {
                powerValue += sumAction(index.from, index.to);
            }
        } else {
            powerValue = sumAction(0, this.length - 1);
        }

        // Convert to the requested unit
        return new UnitValue(new UnitValue(Math.sqrt(powerValue), Pressure.pascal).toUnit(unit), unit);
    }

    /**
     * Computes the total power value of this weighted spectrum
     * @param unit
     * @param range
     */
    public toPowerValue(unit: Unit, range?: Range): UnitValue {
        return this.toPowerValueOfRanges(unit, range ? [range] : undefined);
    }

    /**
     * Creates a copy of this WeightedSpectrum
     */
    public copy(): WeightedSpectrum {
        const originalDataPoints: Point2D[] = [];
        for (let i = 0; i < this.originalDataPoints.length; i++) {
            originalDataPoints.push({
                x: this.originalDataPoints[i].x,
                y: this.originalDataPoints[i].y,
            });
        }

        return new WeightedSpectrum(
            this._weighting,
            this._weightingLowerBound,
            this.xUnit,
            this.yUnit,
            originalDataPoints,
            this.properties.timeRange
        );
    }

    /**
     * Converts this WeightedSpectrum to a WeightedSpectrum with specified units
     * @param xUnit The unit for the new WeightedSpectrum's x data
     * @param yUnit The unit for the new WeightedSpectrum's y data
     */
    public convertToUnits(xUnit: Unit, yUnit: Unit): WeightedSpectrum {
        return new WeightedSpectrum(
            this.weighting,
            this._weightingLowerBound,
            xUnit,
            yUnit,
            super.convertToUnits(xUnit, yUnit).data,
            this.properties.timeRange
        );
    }

    //#region Helper Methods
    private applyWeighting(): void {
        this.data = [];
        for (let i = 0; i < this.originalDataPoints.length; i++) {
            const originalXUnitValue = new UnitValue(this.originalDataPoints[i].x, this.xUnit);
            const weight = this.weighting.getWeight(originalXUnitValue, this.yUnit, this._weightingLowerBound);
            const newDataPoints: Point2D = {
                x: this.originalDataPoints[i].x,
                y: this.originalDataPoints[i].y * weight,
            };

            // we only want to show valid numbers. Example: A-weighting can result in infinite values.
            if (weight !== 0) this.data.push(newDataPoints);
        }
    }
    //#endregion
}
